import {
  DELIVERY_SORT_ORDER,
  DELIVERY_SORT_ORDER_CBE,
  ORDERS_COLUMNS_LABELS,
  OrdersColumnsLabels,
} from '@/pages/Orders/constants'
import { ColumnSortingInverted, OrdersByColumn } from '@/pages/Orders/types'
import { getOrderColumnByStatus } from '@/pages/Orders/utils'
import { getScheduledOrdersKanban } from '@/pages/ScheduledOrders/utils'
import { DeliveryType, OrderStatus } from '@/resources/enums'
import { ActiveOrder } from '@/resources/types'
import { isCbe } from '@/services/orders'

export const getSortedOrders = (
  orders: ActiveOrder[],
  isColumnSortingInverted: ColumnSortingInverted,
  isPriorityOn: boolean
) => {
  const ordersByColumn: OrdersByColumn = getInitialOrderColumns()

  const ordersNotScheduled = orders?.filter(
    ({ delivery, checkoutType }) =>
      delivery.type !== DeliveryType.SCHEDULED && !isCbe(checkoutType)
  )
  const sortedScheduledOrders = getScheduledOrdersKanban({
    orders,
    includeChoppOrders: true,
    includeNewOrders: true,
  })
  handlePriorityOrders(ordersNotScheduled, isPriorityOn)

  collectByOrderByStatus(
    [...ordersNotScheduled, ...sortedScheduledOrders],
    ordersByColumn
  )

  Object.values(ORDERS_COLUMNS_LABELS).forEach(column => {
    const columnOrders = ordersByColumn[column]
    const regularOrders = columnOrders
      .filter(order => !isPracticeOrder(order))
      .sort(sortByDeliveryTypeAndTime)
    const practiceOrders = columnOrders.filter(isPracticeOrder)

    if (isColumnSortingInverted[column]) regularOrders.reverse()

    ordersByColumn[column] = [...regularOrders, ...practiceOrders]
  })

  return ordersByColumn
}

const getInitialOrderColumns = (): OrdersByColumn => ({
  [OrdersColumnsLabels.NEW_ORDERS]: [],
  [OrdersColumnsLabels.IN_SEPARATION_ORDERS]: [],
  [OrdersColumnsLabels.ON_GOING_ORDERS]: [],
})

const collectByOrderByStatus = (
  orders: ActiveOrder[],
  ordersByColumn: OrdersByColumn
) => {
  orders.forEach(order => {
    const orderColumn = getOrderColumnByStatus(order.status as OrderStatus)
    if (orderColumn) {
      ordersByColumn[orderColumn].push(order)
    }
  })
}

const handlePriorityOrders = (orders: ActiveOrder[], isPriorityOn: boolean) => {
  if (!isPriorityOn) return
  orders.forEach(order => {
    if (
      !isPracticeOrder(order) &&
      order.delivery.type === DeliveryType.RECEIVE &&
      order.customer.isFirstPurchase
    ) {
      order.delivery.type = DeliveryType.TURBO
    }
  })
}

const sortByDeliveryTypeAndTime = (
  orderA: ActiveOrder,
  orderB: ActiveOrder
) => {
  const isCbeOrder = isCbe(orderA.checkoutType)
  const sortOrder = isCbeOrder ? DELIVERY_SORT_ORDER_CBE : DELIVERY_SORT_ORDER

  const typeComparison =
    sortOrder[getDeliveryType(orderA)] - sortOrder[getDeliveryType(orderB)]

  if (typeComparison !== 0) return typeComparison
  if (isScheduledOrder(orderA)) return 0

  if (isCbeOrder && isReceiveOrPickUpOrder(orderA))
    return sortByDeliveryDate(orderA, orderB)

  if (isPendingOrder(orderA)) {
    return orderA.expiringTime - orderB.expiringTime
  }
  return orderB.elapsedTime - orderA.elapsedTime
}

const sortByDeliveryDate = (orderA: ActiveOrder, orderB: ActiveOrder) => {
  const startDateA = orderA.delivery.deliveryDate?.startDate
  const startDateB = orderB.delivery.deliveryDate?.startDate

  if (!startDateA && !startDateB) return 0
  if (!startDateA) return 1
  if (!startDateB) return -1

  return new Date(startDateA).getTime() - new Date(startDateB).getTime()
}

const getDeliveryType = (order: ActiveOrder) => {
  const hasChopp = isCbe(order.checkoutType)
  return hasChopp && order.delivery.type !== DeliveryType.PICKUP
    ? DeliveryType.SCHEDULED
    : order.delivery.type
}

const isPracticeOrder = (order: ActiveOrder) => order.isPracticeOrder === true

const isPendingOrder = (order: ActiveOrder) => {
  const status = order.status as OrderStatus
  return status === OrderStatus.PENDING || status === OrderStatus.TRANSFERED
}

const isScheduledOrder = (order: ActiveOrder) =>
  order.delivery.type === DeliveryType.SCHEDULED

const isReceiveOrPickUpOrder = (order: ActiveOrder) =>
  order.delivery.type === DeliveryType.RECEIVE ||
  order.delivery.type === DeliveryType.PICKUP
